import type { PrebookingValidationErrors } from '~/composables/usePrebookingValidation'
import { defineStore } from 'pinia'
import { useOfferStore } from '~/stores/offer'
import { useRegistrationFormStore } from '~/stores/registration-form'
import { clearOidcSessionData } from '~/utils/prebooking-constants'

interface State {
  firstName?: string
  lastName?: string
  emailAddress?: string
  countryOfResidence?: string
  phoneNumber?: string
  // Address fields — only collected for new users (CCT-2060)
  addressStreet?: string
  addressCity?: string
  addressZip?: string
  password?: string
  termsAndConditionsAccepted: boolean
  openFinalDialog: boolean
  isNewCustomer: boolean
  isNewUser: boolean | null
  isCheckingUser: boolean
  // Validation errors for new-user-only fields
  newUserFieldErrors: PrebookingValidationErrors
  // Registration check tracking (CCT-2060)
  registrationCheckCompleted: boolean
  checkedEmail?: string
  checkedPhone?: string
  keycloakRegistrationStarted: boolean
}

export const usePersonalStore = defineStore('personal', {
  state: (): State => ({
    firstName: undefined,
    lastName: undefined,
    emailAddress: undefined,
    countryOfResidence: undefined,
    phoneNumber: undefined,
    addressStreet: undefined,
    addressCity: undefined,
    addressZip: undefined,
    password: undefined,
    termsAndConditionsAccepted: false,
    openFinalDialog: false,
    isNewCustomer: true,
    isNewUser: null,
    isCheckingUser: false,
    newUserFieldErrors: {},
    registrationCheckCompleted: false,
    checkedEmail: undefined,
    checkedPhone: undefined,
    keycloakRegistrationStarted: false,
  }),
  actions: {
    finalOverlayDialogToggle(): boolean {
      this.openFinalDialog = !this.openFinalDialog
      return this.openFinalDialog
    },
    setIsNewCustomer(shouldRegister: boolean): boolean {
      this.isNewCustomer = shouldRegister
      return this.finalOverlayDialogToggle()
    },
    setIsNewUser(value: boolean) {
      this.isNewUser = value
      this.newUserFieldErrors = {}
      // Clear registration-only fields when switching back to existing user
      if (!value) {
        this.addressStreet = undefined
        this.addressCity = undefined
        this.addressZip = undefined
        this.password = undefined
      }
    },
    recordRegistrationCheck(email: string, phone: string, isNewUser: boolean) {
      this.emailAddress = email
      this.phoneNumber = phone
      this.checkedEmail = email
      this.checkedPhone = phone
      this.isNewUser = isNewUser
      this.registrationCheckCompleted = true
    },
    invalidateRegistrationCheck() {
      this.isNewUser = null
      this.registrationCheckCompleted = false
      this.keycloakRegistrationStarted = false
    },
    resetAfterBooking() {
      this.isNewUser = null
      this.registrationCheckCompleted = false
      this.checkedEmail = undefined
      this.checkedPhone = undefined
      this.keycloakRegistrationStarted = false

      // Clear OIDC registration session data
      clearOidcSessionData()

      // Reset all Pinia stores
      this.$reset()
      useOfferStore().$reset()
      useRegistrationFormStore().$reset()

      // Drop the persisted snapshot too — otherwise the pre-booking data
      // gets rehydrated from sessionStorage next time the app initialises
      // in this tab, even though the in-memory reset above just ran (CCT-2060)
      useNuxtApp().$clearPersistedState()
    },
    markKeycloakRegistrationStarted() {
      this.keycloakRegistrationStarted = true
    },
    normalizeInputs() {
      this.firstName = capitalize(this.firstName)
      this.lastName = capitalize(this.lastName)
      this.emailAddress = this.emailAddress?.toLowerCase()
    },
  },
})

function capitalize(word?: string): string | undefined {
  if (!word)
    return undefined
  return word[0].toUpperCase() + word.slice(1).toLowerCase()
}
